import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, MessagesSquare, HelpCircle, ReceiptText, Download, Trash2, ShieldAlert, X, ChevronRight } from "lucide-react";
import {
  portalConversations, portalConversationDetail, portalUnanswered,
  portalInvoices, portalExport, portalDataDelete,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const Spin = () => <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-brand" size={24} /></div>;

function Head({ title, sub }) {
  return (
    <div className="mb-8">
      <h1 className="font-heading text-3xl font-extrabold text-slate-900 tracking-tight">{title}</h1>
      {sub && <p className="text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

function Empty({ icon: Icon, title, sub, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-10 text-center" data-testid={testid}>
      <Icon className="mx-auto text-brand mb-3" size={28} />
      <p className="text-slate-600 font-medium">{title}</p>
      {sub && <p className="text-slate-400 text-sm mt-1">{sub}</p>}
    </div>
  );
}

// ── AI Conversations ────────────────────────────────────────────────
export function Conversations() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null); // {session_id, bot_name, messages}
  const [loadingThread, setLoadingThread] = useState(false);

  useEffect(() => { portalConversations().then(setData).catch(() => setData({ conversations: [] })); }, []);

  const openThread = async (sid) => {
    setLoadingThread(true);
    try { setActive(await portalConversationDetail(sid)); }
    catch { toast.error("Could not load that conversation."); }
    finally { setLoadingThread(false); }
  };

  if (!data) return <Spin />;
  const items = data.conversations || [];

  return (
    <>
      <Head title="AI Conversations" sub="Every chat your AI assistants have had with visitors." />
      {items.length === 0 ? (
        <Empty icon={MessagesSquare} title="No conversations yet." sub="Once visitors chat with your assistants, threads appear here." testid="portal-conversations-empty" />
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl card-shadow overflow-hidden" data-testid="portal-conversations-list">
          {items.map((c) => (
            <button key={c.session_id} onClick={() => openThread(c.session_id)} data-testid="portal-conversation-row"
              className="w-full flex items-center gap-4 px-5 py-4 border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors text-left">
              <div className="w-9 h-9 rounded-full bg-blue-50 text-brand flex items-center justify-center shrink-0"><MessagesSquare size={16} /></div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-800 truncate">{c.bot_name}</span>
                  <span className="text-[11px] text-slate-400 shrink-0">· {c.messages} messages</span>
                </div>
                <p className="text-xs text-slate-500 truncate mt-0.5">{c.last_message || "—"}</p>
              </div>
              <ChevronRight size={16} className="text-slate-300 shrink-0" />
            </button>
          ))}
        </div>
      )}

      {active && (
        <div className="fixed inset-0 z-[70] bg-slate-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={() => setActive(null)} data-testid="portal-conversation-modal">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white w-full max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div><p className="font-semibold text-slate-800">{active.bot_name}</p><p className="text-xs text-slate-400">Conversation transcript</p></div>
              <button onClick={() => setActive(null)} data-testid="portal-conversation-close" className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-slate-50">
              {loadingThread ? <Spin /> : (active.messages || []).map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-brand text-white" : "bg-white border border-slate-200 text-slate-700"}`}>
                    {m.content}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}

// ── Unanswered Questions ───────────────────────────────────────────
export function Unanswered() {
  const [data, setData] = useState(null);
  useEffect(() => { portalUnanswered().then(setData).catch(() => setData({ questions: [] })); }, []);
  if (!data) return <Spin />;
  const items = data.questions || [];
  return (
    <>
      <Head title="Unanswered Questions" sub="Questions your AI couldn't confidently answer — great prompts to expand its knowledge." />
      {items.length === 0 ? (
        <Empty icon={HelpCircle} title="No knowledge gaps found." sub="Your assistants have been answering everything so far." testid="portal-unanswered-empty" />
      ) : (
        <div className="space-y-3" data-testid="portal-unanswered-list">
          {items.map((q, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-2xl card-shadow p-4 flex items-start gap-3" data-testid="portal-unanswered-row">
              <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0"><HelpCircle size={16} /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800">{q.question || q.text || q.q}</p>
                <p className="text-xs text-slate-400 mt-0.5">{q.bot_name}</p>
              </div>
              {q.count > 1 && <span className="text-[11px] font-bold text-amber-700 bg-amber-50 rounded-full px-2 py-0.5 shrink-0">asked {q.count}×</span>}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ── Invoices ────────────────────────────────────────────────────────
export function Invoices() {
  const [data, setData] = useState(null);
  useEffect(() => { portalInvoices().then(setData).catch(() => setData({ invoices: [] })); }, []);
  if (!data) return <Spin />;
  const items = data.invoices || [];
  const sym = data.symbol || "$";
  return (
    <>
      <Head title="Invoices" sub="Your billing history and receipts." />
      {data.demo && (
        <div className="mb-5 inline-flex items-center gap-2 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> SAMPLE HISTORY — real invoices appear here after a paid subscription
        </div>
      )}
      {items.length === 0 ? (
        <Empty icon={ReceiptText} title="No invoices yet." sub="Upgrade to a paid plan to start receiving invoices." testid="portal-invoices-empty" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-5"><p className="overline">Plan</p><p className="font-heading text-2xl font-extrabold mt-2 capitalize">{data.plan}</p></div>
            <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-5"><p className="overline">Total Paid</p><p className="font-heading text-2xl font-extrabold mt-2 text-green-600">{sym}{data.total_paid}</p></div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl card-shadow overflow-hidden" data-testid="portal-invoices-list">
            <div className="hidden md:grid grid-cols-4 gap-4 px-6 py-3 border-b border-slate-100 text-[11px] font-bold uppercase tracking-wide text-slate-400">
              <span>Invoice</span><span>Date</span><span>Description</span><span className="text-right">Amount</span>
            </div>
            {items.map((inv) => (
              <div key={inv.id} className="grid grid-cols-2 md:grid-cols-4 gap-4 px-6 py-4 border-b border-slate-50 last:border-0 items-center" data-testid="portal-invoice-row">
                <div><p className="font-mono text-xs text-slate-700">{inv.id}</p>
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${inv.status === "paid" ? "text-green-700 bg-green-50" : "text-amber-700 bg-amber-50"}`}>{inv.status}</span>
                </div>
                <span className="text-sm text-slate-600">{inv.date}</span>
                <span className="text-sm text-slate-500 hidden md:block truncate">{inv.description}</span>
                <span className="text-right font-heading font-bold text-slate-900">{sym}{inv.total ?? inv.amount}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

// ── Data & Privacy ─────────────────────────────────────────────────
export function Privacy() {
  const { user, logout } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [confirm, setConfirm] = useState(null); // "chatbots" | "account"
  const [busy, setBusy] = useState(false);

  const exportData = async () => {
    setExporting(true);
    try {
      const d = await portalExport();
      const blob = new Blob([JSON.stringify(d, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `websitetech-data-export-${Date.now()}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Your data export has downloaded.");
    } catch { toast.error("Export failed. Please try again."); }
    finally { setExporting(false); }
  };

  const runDelete = async () => {
    setBusy(true);
    try {
      await portalDataDelete({ scope: confirm, confirm: true });
      if (confirm === "account") {
        toast.success("Your account and data have been deleted.");
        logout(); window.location.href = "/";
      } else {
        toast.success("Your chatbot data has been deleted.");
        setConfirm(null);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Deletion failed."); }
    finally { setBusy(false); }
  };

  const isAdmin = user?.role === "admin";

  return (
    <>
      <Head title="Data & Privacy" sub="You're in control of your data. Export or delete it anytime." />
      <div className="space-y-5 max-w-2xl">
        <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-blue-50 text-brand flex items-center justify-center shrink-0"><Download size={18} /></div>
            <div className="flex-1">
              <h3 className="font-heading text-lg font-bold text-slate-900">Export my data</h3>
              <p className="text-sm text-slate-500 mt-1">Download everything we hold — profile, chatbots, conversations, leads and more — as a JSON file.</p>
              <button onClick={exportData} disabled={exporting} data-testid="privacy-export-btn"
                className="btn-primary rounded-lg px-5 py-2.5 text-sm mt-4 inline-flex items-center gap-2 disabled:opacity-60">
                {exporting ? <Loader2 className="animate-spin" size={16} /> : <Download size={15} />} Download my data
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white border border-red-200 rounded-2xl card-shadow p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-red-50 text-red-600 flex items-center justify-center shrink-0"><ShieldAlert size={18} /></div>
            <div className="flex-1">
              <h3 className="font-heading text-lg font-bold text-slate-900">Delete my data</h3>
              <p className="text-sm text-slate-500 mt-1">These actions are permanent and cannot be undone.</p>
              {isAdmin ? (
                <p className="text-xs text-slate-400 mt-3">Admin accounts are protected and can't be deleted here.</p>
              ) : (
                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                  <button onClick={() => setConfirm("chatbots")} data-testid="privacy-delete-bots-btn"
                    className="rounded-lg px-4 py-2.5 text-sm font-medium border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-2">
                    <Trash2 size={15} /> Delete chatbot data
                  </button>
                  <button onClick={() => setConfirm("account")} data-testid="privacy-delete-account-btn"
                    className="rounded-lg px-4 py-2.5 text-sm font-semibold bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-2">
                    <Trash2 size={15} /> Close my account
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {confirm && (
        <div className="fixed inset-0 z-[70] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => !busy && setConfirm(null)} data-testid="privacy-confirm-modal">
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white w-full max-w-md rounded-2xl shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="w-11 h-11 rounded-full bg-red-50 text-red-600 flex items-center justify-center mb-4"><ShieldAlert size={20} /></div>
            <h3 className="font-heading text-xl font-bold text-slate-900">
              {confirm === "account" ? "Close your account?" : "Delete all chatbot data?"}
            </h3>
            <p className="text-sm text-slate-500 mt-2">
              {confirm === "account"
                ? "This permanently deletes your account, chatbots, conversations, websites, projects and all associated data. You'll be logged out."
                : "This permanently deletes all your chatbots and their conversations, leads and knowledge. Your account stays active."}
            </p>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setConfirm(null)} disabled={busy} className="flex-1 rounded-lg px-4 py-2.5 text-sm font-medium border border-slate-200 text-slate-600">Cancel</button>
              <button onClick={runDelete} disabled={busy} data-testid="privacy-confirm-delete"
                className="flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold bg-red-600 text-white hover:bg-red-700 inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 className="animate-spin" size={16} /> : <Trash2 size={15} />} Yes, delete
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}
